import { useState, useEffect } from 'react';
import { useData } from "../backend/firebase";
import { db } from "../backend/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom'; 
import SEO from '../components/SEO';
import { getImagePath } from '../utils/imageRegistry';

const Schedule = () => {
  const { schedule, teams, user, userRole, toggleCommentary, allLineups } = useData();
  const [expandedId, setExpandedId] = useState(null); 
  const [now, setNow] = useState(DateTime.now());
  
  const [filterTeam, setFilterTeam] = useState('ALL');
  
  const [selectedDate, setSelectedDate] = useState(() => {
    const start = DateTime.fromISO('2026-03-10');
    const end = DateTime.fromISO('2026-04-02');
    return (now >= start && now <= end) ? now : start;
  });


  useEffect(() => {
      const timer = setInterval(() => setNow(DateTime.now()), 60000);
      return () => clearInterval(timer);
  }, []);

  const TeamIcon = ({ team, size = 40 }) => {
      const innerSize = size - 6; 
      const wrapperStyle = {
          width: size, height: size, borderRadius: '50%',
          border: `2px solid ${team?.color || '#555'}`,
          padding: 2, background: 'rgba(255,255,255,0.05)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, overflow: 'hidden'
      };
      
      let InnerElement = <div style={{width: innerSize, height: innerSize, background: team?.color || '#333', borderRadius: '50%'}} />;
      if (team?.logoUrl) InnerElement = <img src={getImagePath(team.logoUrl)} alt={team.name} style={{width: innerSize, height: innerSize, objectFit: 'cover', borderRadius: '50%'}} />;
      else if (team?.logo) InnerElement = <img src={`data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}`} alt={team.name} style={{width: innerSize, height: innerSize, objectFit: 'cover', borderRadius: '50%'}} />;

      return <div style={wrapperStyle}>{InnerElement}</div>;
  };

  const generateDates = (startStr, endStr) => {
    let current = DateTime.fromISO(startStr);
    const end = DateTime.fromISO(endStr);
    const dates = [];
    while (current <= end) { dates.push(current); current = current.plus({ days: 1 }); }
    return dates;
  };

  const roundRobinDates = generateDates('2026-03-10', '2026-03-16');
  const finalsDates = generateDates('2026-03-20', '2026-03-22');

  const isFinals = selectedDate >= DateTime.fromISO('2026-03-20');
  const canCommentate = ['admin', 'captain', 'commentator'].includes(userRole?.role);

  const dayEvents = schedule.filter(e => {
      const eDate = DateTime.fromISO(e.startTime, { zone: 'America/Los_Angeles' }).toLocal();
      if (!selectedDate.hasSame(eDate, 'day')) return false;

      if (filterTeam !== 'ALL') {
          const t1 = teams.find(t => t.id.toString() === e.team1?.toString());
          const t2 = teams.find(t => t.id.toString() === e.team2?.toString());
          if (t1?.name !== filterTeam && t2?.name !== filterTeam) return false;
      }
      return true;
  }).sort((a,b) => DateTime.fromISO(a.startTime) - DateTime.fromISO(b.startTime));

  const DateButton = ({ date, label }) => {
      const isActive = date.hasSame(selectedDate, 'day');
      return (
          <button 
            onClick={() => { setSelectedDate(date); setExpandedId(null); }}
            style={{
                background: isActive ? '#6C5DD3' : 'rgba(255,255,255,0.05)',
                border: isActive ? '1px solid #6C5DD3' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, padding: '10px 15px', minWidth: 80, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0,
                color: 'white', transition: '0.2s'
            }}
          >
              <span style={{fontSize: 10, opacity: 0.7, textTransform: 'uppercase'}}>{date.toFormat('ccc')}</span>
              <span style={{fontSize: 16, fontWeight: 'bold'}}>{date.day}</span>
              <span style={{fontSize: 9, color: label==='Finals'?'#FFD700':'#26A65B', marginTop: 2, fontWeight:'bold'}}>{label}</span>
          </button>
      )
  };

  const PlayerBadge = ({ name, color, customStyle }) => (
      <div className="player-badge" style={{ 
          background: 'rgba(255,255,255,0.03)', 
          border: `1px solid ${color}`, 
          color: '#fff', 
          padding: '4px 6px', // Shorter height
          borderRadius: 6, 
          fontSize: 11, 
          textAlign: 'center', 
          fontWeight: 'bold',
          whiteSpace: 'nowrap', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          boxShadow: `0 2px 8px rgba(0,0,0,0.2)`,
          width: 90, // Fixed wider width
          ...customStyle
      }}>
          {name || '-'}
      </div>
  );

  // Narrower bounds set for tables so they sit nicely
  const MiniRosterGrid = ({ tNS, tEW, cNS, cEW, title }) => (
      <div className="mini-roster-wrapper" style={{ 
          flex: '0 0 200px', // STRICT LOCK: Prevents columns from drifting
          background: 'rgba(0,0,0,0.2)', 
          border: '1px solid rgba(255,255,255,0.05)', 
          borderRadius: 8, 
          padding: '12px 10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
      }}>
          <div style={{ textAlign: 'center', marginBottom: 8, fontSize: 9, color: '#8F9EB5', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>
              {title}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              {/* North */}
              <PlayerBadge name={tNS.north} color={cNS} />
              
              {/* West & East (Restored gap and rounded corners) */}
              <div style={{ display: 'flex', gap: 4 }}>
                  <PlayerBadge name={tEW.west} color={cEW} />
                  <PlayerBadge name={tEW.east} color={cEW} />
              </div>

              {/* South */}
              <PlayerBadge name={tNS.south} color={cNS} />
          </div>
      </div>
  );

  return (
    <div className="PageLayout" style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <SEO title="Schedule" description="View the full match schedule for Bridge Brawl 2026." url="/schedule" />
        
        <style>{`
            .page-header-title {
                display: block;        
                text-align: center;    
                width: 100%;
                font-size: 2.2rem;
                font-weight: 800;
                color: #FFFFFF;
                margin: 10px 0 15px 0;
                text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            }

            .match-row-flex {
                display: flex;
                flex-wrap: nowrap;
                align-items: stretch;
                gap: 15px;
                width: 100%;
                margin-bottom: 15px;
            }
            
            /* UPDATED COLUMN LOGIC */
            .match-left-card {
                flex: 1 1 auto; /* Expands to fill available space */
                display: flex;
                flex-direction: column;
                justify-content: center;
                padding: 15px 20px;
                cursor: pointer;
                transition: transform 0.2s, box-shadow 0.2s;
            }
            .match-left-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 10px 20px rgba(0,0,0,0.5) !important;
            }
            
            .match-center-card {
                flex: 0 0 440px; /* Locked Width: Just enough to comfortably fit the two tables */
                display: flex;
                gap: 15px;
                padding: 10px 15px;
                align-items: center;
                justify-content: center;
            }
            
            .match-right-card {
                flex: 0 0 130px; /* Locked Width: Protects the commentary block */
                padding: 10px 15px;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            /* MOBILE RESPONSIVE RULES */
            @media (max-width: 1000px) {
                .match-row-flex {
                    flex-direction: column;
                    gap: 10px;
                }
                .match-left-card, .match-center-card, .match-right-card {
                    flex: 1 1 auto;
                    width: 100%;
                }
                .match-center-card {
                    flex-direction: row;
                    flex-wrap: wrap;
                }
                .match-right-card {
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                }
                .commentator-text {
                    text-align: left !important;
                    margin-bottom: 0 !important;
                }
            }
            @media (max-width: 600px) {
                /* 1. Shrink the Matchup Card */
                .match-left-card { padding: 12px 8px !important; }
                .team-name-text { font-size: 11px !important; }
                .time-center-block { flex: 0 0 50px !important; transform: scale(0.85); }

                /* 2. Stack and Shrink the Tables */
                .match-center-card { 
                    flex-direction: column !important; /* Forces tables to stack vertically */
                    padding: 15px 10px !important; 
                    gap: 15px !important; 
                }
                .mini-roster-wrapper { 
                    flex: 1 1 auto !important; 
                    width: 100% !important; 
                    max-width: 260px !important; 
                    padding: 10px 5px !important; 
                }
                .player-badge { 
                    width: 70px !important; /* Squish the badges so they fit */
                    padding: 3px !important; 
                    font-size: 9px !important; 
                }

                /* 3. Re-center Commentary */
                .match-right-card { 
                    flex-direction: column !important; 
                    padding: 15px 10px !important; 
                    gap: 10px !important; 
                }
                .ContentWrapper {
                    height: 100dvh !important; 
                }
                
                /* Shrink the top elements to give the schedule more room */
                .page-header-title {
                    font-size: 1.6rem !important;
                    margin: 5px 0 10px 0 !important;
                }
                .date-nav-card {
                    padding: 8px 10px !important;
                    margin-bottom: 10px !important;
                }
                .filter-bar {
                    margin-bottom: 10px !important;
                }
                
                /* Shrink the inner header and force the main card to be tall */
                .schedule-main-card {
                    min-height: 65vh !important; 
                }
                .schedule-date-header {
                    padding: 12px 15px !important;
                }
                .schedule-date-header h2 {
                    font-size: 18px !important;
                }
            }
        `}</style>

        <div className="ContentWrapper" style={{
            flex: 1, /* Takes up exactly the remaining space, no more, no less */
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden', 
            maxWidth: 1400, 
            margin: '0 auto',
            width: '100%',
            paddingBottom: '25px', /* This is what creates your clean gap at the bottom */
            boxSizing: 'border-box' /* CRITICAL: Stops the padding from expanding the overall height */
        }}>
            
            <div style={{ flexShrink: 0 }}>
                <h1 className="page-header-title">Bridge Brawl Schedule</h1>
            </div>
            
            <div className="GlassCard date-nav-card" style={{
                padding: '12px 20px', marginBottom: 15, flexShrink: 0,
                background: 'linear-gradient(145deg, #1A1C29 0%, rgba(30, 34, 45, 0.95) 100%)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
                <div style={{display: 'flex', justifyContent: 'space-between', overflowX: 'auto', gap: 10}}>
                    {roundRobinDates.map(d => <DateButton key={d.toISO()} date={d} label="Round Robin" />)}
                    <div style={{width: 1, background: 'rgba(255,255,255,0.1)', margin: '0 10px'}}></div>
                    {finalsDates.map(d => <DateButton key={d.toISO()} date={d} label="Finals" />)}
                </div>
            </div>

            <div className="filter-bar" style={{ display: 'flex', gap: 10, marginBottom: 15, flexShrink: 0 }}>
                <select className="DarkInput" style={{ margin: 0, width: 200, padding: '8px 12px' }} value={filterTeam} onChange={e => setFilterTeam(e.target.value)}>
                    <option value="ALL">All Creators / Teams</option>
                    {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
            </div>

            <div className="GlassCard" style={{
                padding: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: 'linear-gradient(145deg, #1A1C29 0%, rgba(30, 34, 45, 0.95) 100%)',
                boxShadow: '0 15px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
                <div className="schedule-date-header" style={{padding: '25px 30px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, textAlign: 'center'}}>
                    <h2 style={{margin: 0, fontSize: 22, color: '#E0E0E0', letterSpacing: 1}}>{selectedDate.toFormat('cccc, MMMM d')}</h2>
                </div>

                <div className="CustomScroll" style={{flex: 1, overflowY: 'auto', padding: '20px'}}>
                    
                    {dayEvents.length === 0 ? (
                        <div style={{textAlign: 'center', padding: 60, color: '#8F9EB5', background: 'rgba(0,0,0,0.2)', borderRadius: 12}}>
                            <h3>No matches match your filters for this date.</h3>
                        </div>
                    ) : (
                        <div style={{display: 'flex', flexDirection: 'column', paddingBottom: 30}}>
                            {dayEvents.map(ev => {
                                const t1 = teams.find(t => t.id.toString() === ev.team1?.toString());
                                const t2 = teams.find(t => t.id.toString() === ev.team2?.toString());
                                const c1 = t1?.color || '#555';
                                const c2 = t2?.color || '#555';
                                
                                const t1Data = allLineups[ev.id]?.team1Lineup || {};
                                const t2Data = allLineups[ev.id]?.team2Lineup || {};

                                const actualStart = DateTime.fromISO(ev.startTime, { zone: 'America/Los_Angeles' }).toLocal();
                                const broadcastStart = actualStart.plus({ minutes: 15 });
                                const actualEnd = ev.endTime ? DateTime.fromISO(ev.endTime, { zone: 'America/Los_Angeles' }).toLocal() : actualStart.plus({ hours: 2 });
                                const broadcastEnd = actualEnd.plus({ minutes: 15 });

                                const isBroadcastLive = now >= broadcastStart && now <= broadcastEnd;
                                const displayTime = userRole ? actualStart : broadcastStart;
                                const timeStr = displayTime.toFormat('h:mm a');
                                const isDelayed = !userRole;
                                const isExpanded = expandedId === ev.id;

                                const cardBaseStyle = {
                                    background: '#151520',
                                    borderRadius: 12,
                                    border: '1px solid rgba(255,255,255,0.05)',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                };

                                return (
                                    <div key={ev.id}>
                                        <div className="match-row-flex">
                                            
                                            {/* LEFT CARD: EXPANDING MATCHUP */}
                                            <div 
                                                className="match-left-card"
                                                onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                                                style={{
                                                    ...cardBaseStyle,
                                                    borderLeft: `5px solid ${c1}`, 
                                                    borderRight: `5px solid ${c2}`,
                                                    background: isExpanded ? 'rgba(255,255,255,0.05)' : '#151520',
                                                    borderTop: isExpanded ? '1px solid #6C5DD3' : cardBaseStyle.border,
                                                    borderBottom: isExpanded ? '1px solid #6C5DD3' : cardBaseStyle.border,
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 15, width: '100%' }}>
                                                    
                                                    {/* Team 1 (flex: 1 balances Team 2 perfectly) */}
                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <TeamIcon team={t1} size={34} />
                                                        <span className="team-name-text" style={{fontWeight: 'bold', fontSize: 16, color: 'white'}}>{t1?.name || "TBA"}</span>
                                                    </div>

                                                    {/* Center Time & Swords */}
                                                    <div className="time-center-block" style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        {isBroadcastLive && !ev.result ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF3366', boxShadow: '0 0 8px #FF3366' }}></div>
                                                                <span style={{ color: '#FF3366', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }}>LIVE</span>
                                                            </div>
                                                        ) : (
                                                            <div style={{fontSize: 16, color: 'rgba(255,255,255,1.0)', marginBottom: 2}}>⚔️</div>
                                                        )}
                                                        
                                                        {ev.result ? (
                                                            <span style={{color: '#FFD700', fontWeight: 'bold', fontSize: 14}}>{ev.result}</span>
                                                        ) : (
                                                            <span style={{fontSize: 13, fontWeight: 'bold', color: isDelayed ? '#FFD700' : '#26A65B'}}>{timeStr}</span>
                                                        )}
                                                        
                                                        {isDelayed && !ev.result ? <span style={{fontSize: 8, color: '#8F9EB5', marginTop: 2, textTransform: 'uppercase'}}>Broadcast</span> : <span style={{fontSize: 8, color: '#8F9EB5', marginTop: 2, textTransform: 'uppercase'}}>Start Time</span>}
                                                    </div>

                                                    {/* Team 2 */}
                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                                                        <span className="team-name-text" style={{fontWeight: 'bold', fontSize: 16, color: 'white', textAlign: 'right'}}>{t2?.name || "TBA"}</span>
                                                        <TeamIcon team={t2} size={34} />
                                                    </div>
                                                </div>
                                                
                                                <div style={{ textAlign: 'center', marginTop: 12, fontSize: 10, color: '#6C5DD3', fontWeight: 'bold' }}>
                                                    {isExpanded ? "▲ HIDE STREAMS" : "▼ SHOW STREAMS"}
                                                </div>
                                            </div>

                                            {/* CENTER CARD: ROSTERS (FIXED WIDTH) */}
                                            <div className="match-center-card" style={cardBaseStyle}>
                                                <MiniRosterGrid tNS={t1Data} tEW={t2Data} cNS={c1} cEW={c2} title="Table 1" />
                                                <MiniRosterGrid tNS={t2Data} tEW={t1Data} cNS={c2} cEW={c1} title="Table 2" />
                                            </div>

                                            {/* RIGHT CARD: COMMENTARY (FIXED WIDTH) */}
                                            {canCommentate && (
                                                <div className="match-right-card" style={cardBaseStyle}>
                                                    <div>
                                                        <div className="commentator-text" style={{ fontSize: '9px', color: '#8F9EB5', marginBottom: '8px', fontWeight: 'bold', textAlign: 'center' }}>
                                                            COMMENTATORS<br/>({ev.commentators?.length || 0} / 2)
                                                        </div>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); toggleCommentary(ev.id, user?.email); }}
                                                            style={{ 
                                                                background: ev.commentators?.includes(user?.email) ? '#E53E3E' : 'rgba(38, 166, 91, 0.1)',
                                                                border: ev.commentators?.includes(user?.email) ? '1px solid #E53E3E' : '1px solid #26A65B',
                                                                color: ev.commentators?.includes(user?.email) ? '#fff' : '#26A65B',
                                                                borderRadius: 6, padding: '8px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 'bold',
                                                                opacity: (ev.commentators?.length >= 2 && !ev.commentators?.includes(user?.email)) ? 0.5 : 1,
                                                                whiteSpace: 'nowrap', transition: '0.2s', width: '100%'
                                                            }}
                                                            disabled={ev.commentators?.length >= 2 && !ev.commentators?.includes(user?.email)}
                                                        >
                                                            {ev.commentators?.includes(user?.email) ? 'Leave' : '🎙️ Sign Up'}
                                                        </button>
                                                    </div>
                                                    
                                                    {ev.commentators && ev.commentators.length > 0 && (
                                                        <div style={{ marginTop: 8, fontSize: 10, color: '#FFD700', textAlign: 'center', lineHeight: 1.4 }}>
                                                            {ev.commentators.map((c, idx) => <div key={idx} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{c.split('@')[0]}</div>)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* EXPANDED ROW: STREAMS ONLY */}
                                        {isExpanded && (
                                            <div className="GlassCard" style={{...cardBaseStyle, padding: '20px 25px', marginBottom: 15}}>
                                                <label style={{fontSize: 12, color: '#8F9EB5', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1}}>Match Perspectives</label>
                                                <div style={{display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10}}>
                                                    {isBroadcastLive && (
                                                        <Link 
                                                            to="/archive"
                                                            style={{
                                                                background: '#FF3366', color: 'white', textDecoration: 'none',
                                                                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 'bold',
                                                                display: 'flex', alignItems: 'center', gap: 8, transition: '0.2s',
                                                                boxShadow: '0 4px 15px rgba(255, 51, 102, 0.4)'
                                                            }}
                                                        >
                                                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }}></div>
                                                            Watch Live on BBO
                                                        </Link>
                                                    )}
                                                    
                                                    {ev.links && ev.links.length > 0 ? ev.links.map((link, i) => (
                                                        <a 
                                                            key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                                                            style={{
                                                                background: '#25283B', border: '1px solid rgba(255,255,255,0.1)', color: 'white', textDecoration: 'none',
                                                                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 'bold',
                                                                display: 'flex', alignItems: 'center', gap: 6, transition: '0.2s'
                                                            }}
                                                        >
                                                            <span>📺</span> {link.label || "Watch Stream"}
                                                        </a>
                                                    )) : (
                                                        !isBroadcastLive && <span style={{color: '#555', fontSize: 13, fontStyle: 'italic'}}>No streams available yet.</span>
                                                    )}
                                                </div>
                                                {ev.description && <p style={{color: '#ccc', fontSize: 13, marginTop: 15, lineHeight: 1.5}}>{ev.description}</p>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
  );
};

export default Schedule;